import React, { useCallback, useMemo, useState } from "react";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import copy from "clipboard-copy";
import { Theme } from "@emotion/react";
import { SxProps } from "@mui/material";

/**
 * Options to control if the tooltip renders.
 */
interface TooltipOptions {
  disableHoverMessage?: boolean;
  disableCopiedMessage?: boolean;
}

interface CopyableTypographyProps {
  text?: string | null;
  subtext?: string | undefined | null;
  variant?: "body1" | "body2" | "caption";
  stopPropagation?: boolean;
  sx?: SxProps<Theme>;
  tooltipOptions?: TooltipOptions;
}

const DEFAULT_HOVER_MESSAGE = "Click to copy";
const DEFAULT_COPIED_MESSAGE = "Copied to clipboard";

function CopyableTypography({
  text,
  subtext,
  variant = "body1",
  stopPropagation = true,
  sx,
  tooltipOptions = {},
}: CopyableTypographyProps) {
  const [copied, setCopied] = useState(false);

  const handleClose = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 200));
    setCopied(false);
  }, []);

  const handleCopy = useCallback(
    async (event: React.MouseEvent) => {
      if (stopPropagation) {
        event.stopPropagation();
      }
      await copy(text ?? "");
      setCopied(true);
    },
    [stopPropagation, text]
  );

  const tooltipTitle = useMemo(() => {
    if (copied && !tooltipOptions.disableCopiedMessage) {
      return DEFAULT_COPIED_MESSAGE;
    }
    if (!tooltipOptions.disableHoverMessage) {
      return DEFAULT_HOVER_MESSAGE;
    }
    return undefined;
  }, [tooltipOptions, copied]);

  return (
    <Tooltip
      placement="bottom-start"
      title={tooltipTitle}
      onClose={handleClose}
    >
      <Typography
        variant={variant}
        component="div"
        onClick={handleCopy}
        style={{ cursor: "pointer" }}
        sx={sx}
      >
        {text}
        {subtext && (
          <Typography
            variant="caption"
            fontStyle="italic"
            color="text.secondary"
          >
            {subtext}
          </Typography>
        )}
      </Typography>
    </Tooltip>
  );
}

export default CopyableTypography;
